import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/modules/auth';
import { UserRole } from '@/lib/types';
import { parseRoleFromSearchParam, ROLE_LABELS } from '@/lib/roles';
import { analytics } from '@/lib/analytics';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const signupSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type LoginFormData = z.infer<typeof loginSchema>;
type SignupFormData = z.infer<typeof signupSchema>;

const AuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'signup');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>(() =>
    parseRoleFromSearchParam(searchParams.get('role'))
  );
  
  const navigate = useNavigate();
  const { signIn, signUp, user, isLoading: authLoading, userRole } = useAuth();

  const roleHomeRoute = (role: string | null): string => {
    switch (role) {
      case 'admin': return '/admin';
      case 'tradie': return '/tradie';
      case 'property_manager': return '/pm';
      case 'tenant': return '/tenant';
      default: return '/dashboard';
    }
  };

  const redirectTo = searchParams.get('redirect');
  // Restrict to internal paths only — block open redirect vectors
  const isSafeRedirect = (url: string | null): url is string => {
    if (!url) return false;
    // Decode percent-encoded chars before checking (e.g. /%2f/ → //)
    let decoded: string;
    try { decoded = decodeURIComponent(url); } catch { return false; }
    return (
      decoded.startsWith('/') &&
      !decoded.startsWith('//') &&
      !decoded.startsWith('\\') &&
      !decoded.includes('://')
    );
  };
  const safeRedirect = isSafeRedirect(redirectTo);

  React.useEffect(() => {
    // Wait for auth to fully resolve (including profile fetch) before navigating.
    // Without this gate, user is set from cached session before profile loads,
    // causing a flash of PendingApprovalScreen on the dashboard.
    if (user && !authLoading) {
      const target = safeRedirect ? redirectTo! : roleHomeRoute(userRole);
      navigate(target, { replace: true });
    }
  }, [user, authLoading, userRole, navigate, safeRedirect, redirectTo]);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const onLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    const { error } = await signIn(data.email, data.password);
    setIsLoading(false);

    if (error) {
      toast.error('Login Failed', {
        description: error.message === 'Invalid login credentials'
          ? 'Invalid email or password. Check your details or run the create-admin script to reset the admin password.'
          : error.message,
      });
    } else {
      analytics.login();
      toast.success('Welcome back!', {
        description: 'You have successfully logged in.',
      });
      // Role may not be available yet (profile still loading), so go to /dashboard
      // and let ProtectedRoute redirect based on role. If role IS available, go direct.
      const target = safeRedirect ? redirectTo! : roleHomeRoute(userRole);
      navigate(target, { replace: true });
    }
  };

  const onSignup = async (data: SignupFormData) => {
    analytics.signupStarted(selectedRole);
    setIsLoading(true);
    const { error } = await signUp(data.email, data.password, data.fullName, selectedRole);
    setIsLoading(false);

    if (error) {
      let message = error.message;
      if (error.message.includes('already registered')) {
        message = 'This email is already registered. Please sign in instead.';
      }
      toast.error('Signup Failed', {
        description: message,
      });
    } else {
      analytics.signupCompleted(selectedRole);
      toast.success('Account Created!', {
        description: 'Welcome to Agentive Property. Let\'s get started!',
      });
      const target = safeRedirect ? redirectTo! : roleHomeRoute(userRole);
      navigate(target, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-card rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <Link to="/" className="flex justify-center mb-8">
            <BrandLogo size="lg" />
          </Link>

          {/* Tab Switcher */}
          <div className="flex bg-muted rounded-lg p-1 mb-8">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                isLogin
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                !isLogin
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign Up
            </button>
          </div>

          {isLogin ? (
            /* Login Form */
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10"
                    {...loginForm.register('email')}
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className="text-sm text-destructive mt-1">
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    {...loginForm.register('password')}
                  />
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-sm text-destructive mt-1">
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <Button type="submit" variant="default" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Sign In <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            /* Signup Form */
            <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
              {/* Role Selector */}
              <div>
                <Label>I am a...</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setSelectedRole('property_manager')}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      selectedRole === 'property_manager'
                        ? 'border-accent bg-accent/10'
                        : 'border-border hover:border-accent/50'
                    }`}
                  >
                    <div className="font-medium text-foreground text-sm">{ROLE_LABELS.property_manager}</div>
                    <div className="text-xs text-muted-foreground">I manage properties</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedRole('tradie')}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      selectedRole === 'tradie'
                        ? 'border-accent bg-accent/10'
                        : 'border-border hover:border-accent/50'
                    }`}
                  >
                    <div className="font-medium text-foreground text-sm">{ROLE_LABELS.tradie}</div>
                    <div className="text-xs text-muted-foreground">I do the work</div>
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="fullName">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="John Smith"
                    className="pl-10"
                    {...signupForm.register('fullName')}
                  />
                </div>
                {signupForm.formState.errors.fullName && (
                  <p className="text-sm text-destructive mt-1">
                    {signupForm.formState.errors.fullName.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="signup-email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10"
                    {...signupForm.register('email')}
                  />
                </div>
                {signupForm.formState.errors.email && (
                  <p className="text-sm text-destructive mt-1">
                    {signupForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="signup-password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    {...signupForm.register('password')}
                  />
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-sm text-destructive mt-1">
                    {signupForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    {...signupForm.register('confirmPassword')}
                  />
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-sm text-destructive mt-1">
                    {signupForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <Button type="submit" variant="default" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Create Account <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By signing up, you agree to our Terms of Service and Privacy Policy
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
